import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardLayout";
import { api, fmtZAR } from "@/lib/api";
import { Users, ClipboardCheck, HandCoins, Ticket, ShieldAlert, TrendingUp, TrendingDown } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <DashboardShell role="admin">
      <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Overview</p>
      <h1 className="font-display mt-2 text-3xl font-bold text-[#0A2540]">FlexiVouch Admin</h1>
      <p className="text-sm text-[#666666] mt-1">Real-time platform liabilities, KYC queue and merchant activity.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        <Stat label="Total merchants" value={stats?.total_merchants ?? "—"} icon={Users} accent="#0A2540" testid="admin-stat-merchants" />
        <Stat label="Active merchants" value={stats?.active_merchants ?? "—"} icon={ShieldAlert} accent="#00C853" testid="admin-stat-active" />
        <Stat label="Pending KYC" value={stats?.pending_kyc ?? "—"} icon={ClipboardCheck} accent="#FF9F1C" testid="admin-stat-kyc" />
        <Stat label="Pending top-ups" value={stats?.pending_topups ?? "—"} icon={HandCoins} accent="#00C4B4" testid="admin-stat-topups" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <BigStat label="Outstanding liability" value={fmtZAR(stats?.outstanding_liability || 0)} icon={Ticket} testid="admin-liability" />
        <BigStat label="Total sold" value={fmtZAR(stats?.total_sold || 0)} icon={TrendingUp} testid="admin-sold" />
        <BigStat label="Total redeemed" value={fmtZAR(stats?.total_redeemed || 0)} icon={TrendingDown} testid="admin-redeemed" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        <QuickLink to="/admin/kyc" title="Review KYC queue" desc="Approve or reject merchant submissions." />
        <QuickLink to="/admin/wallet" title="Approve top-ups" desc="Verify proof of payment and credit wallets." />
        <QuickLink to="/admin/vouchers" title="Voucher liabilities" desc="Audit active stored-value balances." />
      </div>
    </DashboardShell>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">{label}</p>
        <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}12`, color: accent }}><Icon size={16} /></span>
      </div>
      <p className="font-display mt-3 text-3xl font-bold text-[#0A2540]">{value}</p>
    </div>
  );
}

function BigStat({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0A2540] text-white rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">{label}</p>
        <Icon size={18} className="text-white/70" />
      </div>
      <p className="font-display mt-3 text-4xl font-bold">{value}</p>
    </div>
  );
}

function QuickLink({ to, title, desc }) {
  return (
    <Link to={to} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 hover:border-[#00C4B4] hover:-translate-y-0.5 transition-all block">
      <p className="font-display text-lg font-semibold text-[#0A2540]">{title}</p>
      <p className="text-sm text-[#666666] mt-1">{desc}</p>
    </Link>
  );
}
